// import node module libraries
import React, { Fragment, useEffect, useMemo, useState } from "react";
import StatRightIcon from "components/StatRightIcon";
import { ReactComponent as StoreCardSvg } from "assets/svgs/store-card.svg";
import { ReactComponent as MoneyCardSvg } from "assets/svgs/money-card.svg";

import { Link, useHistory } from "react-router-dom";

import { Col, Row, Spinner, FormSelect } from "react-bootstrap";

import { useDispatch } from "react-redux";
import { ToastContainer, toast } from "react-toastify";

import { useSelector } from "react-redux";

import TransactionTable from "components/TransactionTable";
import { useGetTransactionsQuery, useLazyGetTransactionsQuery } from "redux/features/user/userApi";
import { pageOffset, listParams, startOfMonth, useCount, useTablePage } from "hooks/useServerPagination";
import MultiSelectionTable from "components/MultiSelectionTable";
import { capitalize, dateString, formatCurrency } from "helper/formatText";
import moment from "moment";
import DataTable from "components/DataTable";
import { TextSearchFilter } from "components/advance-table/filters";
import CustomButton from "components/CustomButton";
import { ChangeTransactionStatus } from "./components/ChangeTransactionStatus";
import { convertJsonStringToObject } from "helper/formatObject";

const Transactions = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { countries } = useSelector((state) => state.userAuth);
  const [statusModal, setStatusModal] = useState(false);
  const [currentType, setCurrentType] = useState("all");
  const typeOptions = [
    { value: "all", label: "All" },
    { value: "shopper_withdrawal", label: "Shopper Withdrawal" },
    { value: "shopper_commission", label: "Shopper Commission" },
    { value: "vendor_payment", label: "Vendor Payment" },
    { value: "order_payment", label: "Order Payment" },
  ];
  const [refresh, setRefresh] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [getTransactions, { data: response, isLoading }] = useLazyGetTransactionsQuery();
  const list = useMemo(() => (response?.data || []).map((x) => convertJsonStringToObject(JSON.parse(JSON.stringify(x)))), [response]);
  const [tableState, onPageChange] = useTablePage();

  // The stat cards cover every transaction type, as before; the type selector
  // only filters the table.
  const pending = useCount(useGetTransactionsQuery, { status: "pending" });
  const settled = useCount(useGetTransactionsQuery, { status: "settled" });
  const settledThisMonth = useCount(useGetTransactionsQuery, { status: "settled", "updatedAt[gte]": startOfMonth() });
  const stats = { pending, settled, settledThisMonth };

  const fetchList = async () => {
    const typeFilter = currentType !== "all" ? { type: currentType } : {};
    const res = await getTransactions({ ...typeFilter, ...listParams(tableState, columns, "-updatedAt") });

    if (res?.error) {
      toast.error(res?.error?.data?.message || "Something went wrong", {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };
  // Bulk status updates aren't offered on this mixed-type list (its button is
  // commented out below); the orders, shoppers and vendors transaction pages do it.
  const updateTransactionStatus = async (status) => {};

  useEffect(() => {
    if (tableState) fetchList();
  }, [refresh, tableState, currentType]);

  const columns = useMemo(
    () => [
      { accessor: "sn", Header: "S/N", disableSortBy: true },
      //   { accessor: "id", Header: "ID" },
      {
        accessor: "name",
        sortField: "type",
        Header: "Name",
        Filter: TextSearchFilter,
        filter: "rankedMatchSorter",
      },
      {
        accessor: "status",
        Header: "Status",
        Cell: ({ value, row }) => {
          return capitalize(value);
        },
        Filter: TextSearchFilter,
        filter: "rankedMatchSorter",
      },
      {
        accessor: "amount",
        Header: "Amount",
        Cell: ({ value, row }) => {
          const currency = row?.original?.order_id?.country ? convertJsonStringToObject(row?.original?.order_id?.country)?.currency : "#";
          return `${formatCurrency(value, 2, row?.original?.type == "order_payment" ? currency?.symbol : undefined)}`;
        },
        Filter: TextSearchFilter,
        filter: "rankedMatchSorter",
      },
      {
        accessor: "order_id",
        disableSortBy: true,
        Header: "Order ID",
        Cell: ({ value, row }) => {
          return value?.order_id;
        },
      },
      {
        accessor: "market",
        disableSortBy: true,
        Header: "Market",
        Filter: TextSearchFilter,
        filter: "rankedMatchSorter",
      },
      {
        accessor: "userName",
        disableSortBy: true,
        Header: "Sender/Recipient",
        Filter: TextSearchFilter,
        filter: "rankedMatchSorter",
      },
      {
        accessor: "userPhone",
        disableSortBy: true,
        Header: "Phone number",
      },
      {
        accessor: "userAccountNumber",
        disableSortBy: true,
        Header: "Account number",
      },
      {
        accessor: "userBankName",
        disableSortBy: true,
        Header: "Bank",
      },
      {
        accessor: "userAccountName",
        disableSortBy: true,
        Header: "Account name",
      },
      {
        accessor: "user_model_name",
        Header: "User Type",
      },
      {
        accessor: "entry",
        Header: "Entry",
        Cell: ({ value, row }) => {
          return capitalize(value);
        },
      },
      {
        accessor: "payment_gateway",
        Header: "Payment method",
        Cell: ({ value, row }) => {
          return capitalize(value?.replace("_", " "));
        },
      },
      {
        accessor: "reference",
        Header: "Reference",
        Filter: TextSearchFilter,
        filter: "rankedMatchSorter",
      },
      {
        accessor: "created_At",
        sortField: "createdAt",
        Header: "Date created",
        Cell: ({ value, row }) => {
          return moment(value).format("ddd MMM DD, yyyy");
        },
      },
      {
        accessor: "updated_At",
        sortField: "updatedAt",
        Header: "Last updated",
        Cell: ({ value, row }) => {
          return moment(value).format("ddd MMM DD, yyyy");
        },
      },
    ],
    [],
  );

  const data = useMemo(() => {
    let filtered = list || [];
    filtered = filtered?.map((x, index) => {
      return {
        ...x,
        sn: pageOffset(response) + index + 1,
        name: capitalize(x?.type?.replace("_", " ")),
        userName: x?.user_id?.firstName + " " + x?.user_id?.lastName,
        userEmail: x?.user_id?.email,
        userPhone: x?.user_id?.phoneNumber?.toString(),
        userBankName: x?.user_id?.bank_name,
        userAccountNumber: x?.user_id?.account_number?.toString(),
        userAccountName: x?.user_id?.account_name,
        market: x?.user_id?.market_id?.name || x?.order_id?.market?.name,
        created_At: dateString(x?.createdAt),
        updated_At: dateString(x?.updatedAt),
      };
    });
    return filtered;
  }, [list, response]);

  useEffect(() => {
    setStatusModal(false);
  }, [selectedRows]);

  const toggleStatusModal = () => {
    setStatusModal(!statusModal);
  };

  const handleTransactionStatusChange = async (status) => {
    await updateTransactionStatus(status);
  };

  if (isLoading) {
    return (
      <Row className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner style={{ color: "#e0bf00" }} />
      </Row>
    );
  }

  return (
    <Fragment>
      <div className="overflow-hidden justify-content-space-around">
        <Row>
          <Col xl={4} lg={6} md={6} sm={12}>
            <StatRightIcon
              title="Pending Transactions"
              value={stats.pending}
              iconComponent={<MoneyCardSvg />}
              iconColorVariant="primary"
              classValue="mb-4"
            />
          </Col>

          <Col xl={4} lg={6} md={6} sm={12}>
            <StatRightIcon
              title="Settled Transactions"
              value={stats.settled}
              iconComponent={<MoneyCardSvg />}
              iconColorVariant="primary"
              classValue="mb-4"
            />
          </Col>

          <Col xl={4} lg={6} md={6} sm={12}>
            <StatRightIcon
              title="Settled this month"
              value={stats.settledThisMonth}
              iconComponent={<MoneyCardSvg />}
              iconColorVariant="primary"
              classValue="mb-4"
            />
          </Col>
        </Row>
      </div>
      <Row>
        {/* <TransactionTable items={list} title="Transactions" detailsRedirect="/transactions/:id" /> */}
        <DataTable
          serverPagination={response}
          onPageChange={onPageChange}
          pageResetKey={currentType}
          nonSelective={true}
          items={list}
          title="Transactions"
          detailsRedirect="/transactions/:id"
          columns={columns}
          data={data}
          excelFileName={"FD_Transactions"}
          excelSheetName={"Transactions"}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          // externalActionButton={
          //   selectedRows?.length > 0 && <CustomButton variant="primary" type="button" text="Update Status" onClick={toggleStatusModal} />
          // }
          externalActionButton1={
            <Col className="d-flex align-items-center">
              {/* Pagination bit */}
              <FormSelect
                value={currentType}
                onChange={(e) => {
                  setCurrentType(e.target.value);
                }}
              >
                {typeOptions.map((size) => (
                  <option key={size?.value} value={size?.value}>
                    {size?.label}
                  </option>
                ))}
              </FormSelect>
            </Col>
          }
        />
      </Row>
      <ChangeTransactionStatus
        isBulk={true}
        show={statusModal && selectedRows?.length > 0}
        close={toggleStatusModal}
        onConfirm={handleTransactionStatusChange}
        isLoading={false}
      />
    </Fragment>
  );
};

export default Transactions;
